import { RotateCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getTestIssueKey, previewPanel, setTestIssueKey } from '@/lib/panels'

// The preview is content rendered the way Jira shows it inside the issue view — light,
// outside the console theme — so the iframe document carries its own colours.
const EMPTY_DOC = (text: string) =>
  `<!doctype html><body style="margin:0;padding:12px;font:13px -apple-system,'Segoe UI',sans-serif;color:#44546F;background:#fff">${text}</body>`

/**
 * Live preview of a SAVED panel on a test issue key. The backend previews only the stored
 * panel (POST /panels/{id}/preview), so unsaved edits never show here — hence "saved version".
 * `auto` — re-render by itself after the key (or `version`) changes, debounced ~1 s.
 */
export function PanelPreview({ panelId, version, auto = false, height = 360, compact = false }: {
  panelId: string
  /** bump to re-render (e.g. after a save) */
  version?: unknown
  auto?: boolean
  height?: number
  /** list pane: smaller header, explicit Preview button */
  compact?: boolean
}) {
  const [key, setKey] = useState(() => getTestIssueKey(panelId))
  const [html, setHtml] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [took, setTook] = useState<number | null>(null)
  const req = useRef(0)

  // another panel selected in the list → its own remembered key
  const [seen, setSeen] = useState(panelId)
  if (seen !== panelId) {
    setSeen(panelId)
    setKey(getTestIssueKey(panelId))
    setHtml(null)
    setError(null)
  }

  const run = useCallback(async (issueKey: string) => {
    const k = issueKey.trim()
    if (!k) return
    const id = ++req.current
    setBusy(true)
    setError(null)
    const t0 = performance.now()
    try {
      const r = await previewPanel(panelId, k)
      if (id !== req.current) return
      setHtml(r.html)
      setTook((performance.now() - t0) / 1000)
      setTestIssueKey(panelId, k)
    } catch (e) {
      if (id === req.current) setError((e as Error).message)
    } finally {
      if (id === req.current) setBusy(false)
    }
  }, [panelId])

  // a remembered key renders straight away
  const last = useRef<{ key: string; version: unknown } | null>(null)
  useEffect(() => {
    const k = getTestIssueKey(panelId)
    if (!k) return
    last.current = { key: k, version }
    void run(k)
  }, [panelId]) // eslint-disable-line react-hooks/exhaustive-deps

  // editor: re-render ~1 s after the key changes or a new version was saved
  useEffect(() => {
    if (!auto || !key.trim()) return
    if (last.current && last.current.key === key && Object.is(last.current.version, version)) return
    const t = setTimeout(() => {
      last.current = { key, version }
      void run(key)
    }, 900)
    return () => clearTimeout(t)
  }, [key, version, auto, run])

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2">
          <span className={compact ? 'sr-only' : 'shrink-0 text-[11.5px] text-text-3'}>Test issue key</span>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void run(key) } }}
            placeholder="e.g. DOM-21044"
            aria-label="Test issue key"
            className="h-7 w-[140px] min-w-0 rounded-md border border-input bg-surface px-2 font-mono text-[12px] text-foreground placeholder:text-text-3"
          />
        </label>
        {compact
          ? (
            <button
              type="button"
              onClick={() => void run(key)}
              disabled={busy || !key.trim()}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-line-strong bg-surface-raised px-2.5 text-[12px] text-foreground transition-colors hover:bg-surface-active disabled:opacity-60"
            >
              Preview
            </button>
          )
          : (
            <button
              type="button"
              onClick={() => void run(key)}
              disabled={busy || !key.trim()}
              aria-label="Refresh preview"
              title="Refresh preview"
              className="inline-flex size-7 items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground disabled:opacity-60"
            >
              <RotateCw aria-hidden className={`size-3.5 ${busy ? 'animate-spin' : ''}`} />
            </button>
          )}
      </div>
      <div className="mb-1.5 flex items-center gap-2 text-[11.5px] text-text-3">
        <span>saved version</span>
        {busy ? <span aria-live="polite">rendering…</span> : took !== null && html && <span>· {took.toFixed(1)} s</span>}
      </div>
      {error && <p role="alert" className="mb-2 text-[12px] text-danger">{error}</p>}
      <iframe
        title="Panel preview"
        srcDoc={html ?? EMPTY_DOC(busy
          ? 'Rendering…'
          : 'Enter an issue key in the panel’s project scope to render a preview.')}
        sandbox=""
        className="w-full rounded-md border border-line"
        style={{ height }}
      />
    </div>
  )
}
